import { MAX_PAYLOAD_BYTES } from './constants';
import { debugLog, logger, sendToTerminal } from '../terminalLogger';
import { LogCategory } from '../../config/logging';

/**
 * Contador de mensagens enviadas (para diagnóstico)
 */
let messagesSentCount = 0;
let messagesFailedCount = 0;
let lastSuccessTime: number | null = null;
let lastFailureTime: number | null = null;

/**
 * Retorna estatísticas de transporte para diagnóstico
 */
export function getTransportStats(): {
  messagesSent: number;
  messagesFailed: number;
  lastSuccess: number | null;
  lastFailure: number | null;
  webviewAvailable: boolean;
  isHttps: boolean;
  hostname: string;
} {
  return {
    messagesSent: messagesSentCount,
    messagesFailed: messagesFailedCount,
    lastSuccess: lastSuccessTime,
    lastFailure: lastFailureTime,
    webviewAvailable: !!(window as any)?.chrome?.webview,
    isHttps: window.location.protocol === 'https:',
    hostname: window.location.hostname
  };
}

/**
 * Verifica se o WebView2 está disponível e pronto para comunicação
 */
export function isWebViewReady(): boolean {
  const webview = (window as any)?.chrome?.webview;
  if (!webview) return false;
  
  // Verificar se postMessage existe
  if (typeof webview.postMessage !== 'function') return false;
  
  return true;
}

export function postJsonToWebView(json: string): boolean {
  if (json.length > MAX_PAYLOAD_BYTES) {
    debugLog('CSHARP_BRIDGE:TRANSPORT_BLOCKED_PAYLOAD_TOO_LARGE', { size: json.length });
    messagesFailedCount++;
    lastFailureTime = Date.now();
    return false;
  }
  
  try {
    // DEBUG: Verificar disponibilidade
    const webviewAvailable = !!(window as any)?.chrome?.webview;
    const origin = typeof window !== 'undefined' ? window.location.origin : 'unknown';
    const isHttps = window.location.protocol === 'https:';
    
    // 🔥 CORREÇÃO CRÍTICA: WebView2 espera OBJETO JavaScript, não string JSON
    const payload = JSON.parse(json);
    
    // ✅ DIAGNÓSTICO: Extrair action para logs
    const action = payload?.action || 'unknown';
    const requestId = payload?.requestId || 'no-id';
    
    logger.debug('🚀 [TRANSPORT DEBUG] Enviando para WebView', {
      available: webviewAvailable,
      origin: origin,
      isHttps,
      payloadSize: json.length,
      action,
      requestId
    });
    
    if (!webviewAvailable) {
      // ✅ MELHORADO: Diagnóstico detalhado quando WebView não está disponível
      messagesFailedCount++;
      lastFailureTime = Date.now();
      
      sendToTerminal('❌ [TRANSPORT] WebView2 NÃO está disponível!', {
        action,
        requestId,
        origin,
        isHttps,
        hostname: window.location.hostname,
        hasChrome: !!(window as any)?.chrome,
        hasWebview: !!(window as any)?.chrome?.webview,
        stats: {
          sent: messagesSentCount,
          failed: messagesFailedCount
        }
      }, LogCategory.ERROR);
      
      return false;
    }
    
    // ✅ ENVIAR OBJETO, NÃO STRING
    (window as any).chrome.webview.postMessage(payload);
    
    // Atualizar estatísticas
    messagesSentCount++;
    lastSuccessTime = Date.now();
    
    logger.debug('✅ [TRANSPORT] Mensagem enviada com sucesso', {
      action,
      requestId,
      messageNumber: messagesSentCount
    });
    
    return true;
  } catch (e) {
    messagesFailedCount++;
    lastFailureTime = Date.now();
    
    // ✅ MELHORADO: Log detalhado de erro
    const errorMessage = e instanceof Error ? e.message : String(e);
    const errorStack = e instanceof Error ? e.stack : undefined;
    
    sendToTerminal('❌ [TRANSPORT] Erro ao enviar mensagem', {
      error: errorMessage,
      stack: errorStack?.split('\n').slice(0, 3).join('\n'),
      jsonLength: json.length,
      jsonPreview: json.substring(0, 200) + '...',
      stats: {
        sent: messagesSentCount,
        failed: messagesFailedCount
      }
    }, LogCategory.ERROR);
    
    logger.error('❌ [TRANSPORT] Erro ao enviar:', e);
    return false;
  }
}
